import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, Alert, TouchableOpacity} from 'react-native';
import {
  Text, Appbar, ActivityIndicator,
  Dialog, Portal, Button, TextInput, IconButton, Snackbar, Divider, FAB,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {measurementApi} from '../../services/api/measurementApi';
import {colors, spacing} from '../../theme';

const CURATED_ICONS = [
  'room-service-outline', 'iron', 'scissors-cutting', 'spray', 'washing-machine',
  'tshirt-crew', 'hanger', 'needle', 'tape-measure', 'brush',
  'palette', 'auto-fix', 'star', 'heart', 'tag',
  'hammer-wrench', 'account-wrench', 'basket', 'store', 'shopping',
];

const ServiceListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();

  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [serviceName, setServiceName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(CURATED_ICONS[0]);
  const [snackbar, setSnackbar] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await measurementApi.listCatalogItems(true, 'SERVICE');
      setServices(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const openAddDialog = useCallback(() => {
    setEditingItem(null);
    setServiceName('');
    setSelectedIcon(CURATED_ICONS[0]);
    setDialogVisible(true);
  }, []);

  const openEditDialog = useCallback((item) => {
    setEditingItem(item);
    setServiceName(item.name);
    setSelectedIcon(item.icon || CURATED_ICONS[0]);
    setDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    const name = serviceName.trim();
    if (!name) return;

    try {
      if (editingItem) {
        await measurementApi.updateCatalogItem(editingItem.id, {
          name,
          icon: selectedIcon,
        });
      } else {
        const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
        await measurementApi.createCatalogItem({
          name,
          code,
          icon: selectedIcon,
          gender: 'BOTH',
          itemType: 'SERVICE',
        });
      }
      setDialogVisible(false);
      setServiceName('');
      setSelectedIcon(CURATED_ICONS[0]);
      setEditingItem(null);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [serviceName, selectedIcon, editingItem, fetchData, t]);

  const handleDelete = useCallback((item) => {
    Alert.alert(
      t('common.delete'),
      `${t('common.delete')} "${item.name}"?`,
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await measurementApi.deleteCatalogItem(item.id);
              await fetchData();
            } catch (e) {
              setSnackbar(e.response?.data?.error?.message || t('common.error'));
            }
          },
        },
      ],
    );
  }, [fetchData, t]);

  if (isLoading && services.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} color={colors.white} />
        <Appbar.Content title={t('service.title')} titleStyle={styles.appbarTitle} />
      </Appbar.Header>

      <ScrollView
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />}>
        {services.length === 0 ? (
          <Text style={styles.emptyText}>{t('service.noServices')}</Text>
        ) : (
          services.map((item, index) => (
            <View key={item.id}>
              <View style={styles.row}>
                <MaterialCommunityIcons
                  name={item.icon || 'room-service-outline'}
                  size={24}
                  color={colors.primary}
                  style={styles.rowIcon}
                />
                <Text style={styles.itemName}>{item.name}</Text>
                <IconButton
                  icon="pencil-outline"
                  size={20}
                  iconColor={colors.textSecondary}
                  onPress={() => openEditDialog(item)}
                  style={styles.actionBtn}
                />
                <IconButton
                  icon="delete-outline"
                  size={20}
                  iconColor={colors.error}
                  onPress={() => handleDelete(item)}
                  style={styles.actionBtn}
                />
              </View>
              {index < services.length - 1 && <Divider />}
            </View>
          ))
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={dialogVisible} onDismiss={() => setDialogVisible(false)} style={{borderRadius: 15}}>
          <Dialog.Title>
            {editingItem ? t('service.editService') : t('service.addService')}
          </Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('service.serviceName')}
              value={serviceName}
              onChangeText={setServiceName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <Text style={styles.iconLabel}>{t('service.selectIcon')}</Text>
            <View style={styles.iconGrid}>
              {CURATED_ICONS.map(iconName => (
                <TouchableOpacity
                  key={iconName}
                  onPress={() => setSelectedIcon(iconName)}
                  style={[
                    styles.iconCell,
                    selectedIcon === iconName && styles.iconCellSelected,
                  ]}>
                  <MaterialCommunityIcons
                    name={iconName}
                    size={24}
                    color={selectedIcon === iconName ? colors.white : colors.text}
                  />
                </TouchableOpacity>
              ))}
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>{t('common.cancel')}</Button>
            <Button mode="contained" onPress={handleSave} style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <FAB
        icon="plus"
        onPress={openAddDialog}
        style={styles.fab}
        color={colors.white}
      />

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  appbarTitle: {color: colors.white},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  rowIcon: {marginRight: spacing.md},
  itemName: {flex: 1, fontSize: 16, fontWeight: '600', color: colors.text},
  actionBtn: {margin: 0},
  dialogInput: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  iconLabel: {fontSize: 14, color: colors.textSecondary, marginBottom: spacing.xs},
  iconGrid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  iconCell: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
  iconCellSelected: {backgroundColor: colors.primary},
  saveButton: {backgroundColor: colors.primary, borderRadius: 10, paddingHorizontal: 10},
});

export default ServiceListScreen;
